const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

class TokenManager {
  private static instance: TokenManager;
  private ACCESS_TOKEN_KEY = 'access_token';
  private REFRESH_TOKEN_KEY = 'refresh_token';

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  storeTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }
}

class ApiClient {
  private tokenManager: TokenManager;
  private isRefreshing: boolean = false;
  private failedQueue: Array<{
    resolve: (value: any) => void;
    reject: (reason: any) => void;
    url: string;
    options: RequestInit;
  }> = [];

  constructor() {
    this.tokenManager = TokenManager.getInstance();
  }

  async refreshAccessToken(): Promise<string> {
    const refreshToken = this.tokenManager.getRefreshToken();
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    console.log('🔄 Refreshing access token...');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Store the new tokens
        this.tokenManager.storeTokens(data.access_token, data.refresh_token);
        
        return data.access_token;
      } else if (response.status === 401) {
        // Refresh token is invalid/expired - redirect to login
        this.tokenManager.clearTokens();
        window.location.href = '/login';
        throw new Error('Session expired. Please log in again.');
      } else {
        throw new Error('Failed to refresh token');
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  }

  async makeRequest(url: string, options: RequestInit = {}): Promise<Response> {
    const accessToken = this.tokenManager.getAccessToken();

    // Add authorization header if token exists
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    // Only set Content-Type for non-FormData bodies
    // For FormData, let the browser set the correct Content-Type with boundary
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const requestOptions: RequestInit = {
      ...options,
      headers,
    };
    
    try {
      const response = await fetch(url, requestOptions);
      
      // If token expired, try to refresh (but not for login endpoint)
      if (response.status === 401 && accessToken && !url.includes('/login')) {
        return await this.handleTokenExpired(url, requestOptions);
      }
      
      return response;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  private async handleTokenExpired(originalUrl: string, originalOptions: RequestInit): Promise<Response> {
    if (this.isRefreshing) {
      // If already refreshing, wait for it to complete
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject, url: originalUrl, options: originalOptions });
      });
    }
    
    this.isRefreshing = true;
    
    try {
      const newAccessToken = await this.refreshAccessToken();
      
      // Retry all queued requests with new token
      this.processQueue(null, newAccessToken);
      
      // Retry the original request with new token
      const newHeaders: Record<string, string> = {
        ...(originalOptions.headers as Record<string, string>),
        'Authorization': `Bearer ${newAccessToken}`,
      };
      
      return await fetch(originalUrl, { ...originalOptions, headers: newHeaders });
      
    } catch (error) {
      this.processQueue(error, null);
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  private processQueue(error: any, token: string | null = null): void {
    this.failedQueue.forEach(({ resolve, reject, url, options }) => {
      if (error) {
        reject(error);
      } else {
        const newHeaders: Record<string, string> = {
          ...(options.headers as Record<string, string>),
          'Authorization': `Bearer ${token}`,
        };
        resolve(fetch(url, { ...options, headers: newHeaders }));
      }
    });
    
    this.failedQueue = [];
  }
}

// Create a singleton instance
const apiClient = new ApiClient();

// Export for external use
export { TokenManager };

export const apiRequest = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<any> => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  try {
    const response = await apiClient.makeRequest(url, options);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

export const authenticatedRequest = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<any> => {
  return apiRequest(endpoint, options);
};

export const authAPI = {
    register: async (userData: {
        email: string;
        password: string;
        full_name: string;
        account_type: string;
        parental_consent_given?: boolean;
    }) => {
        return apiRequest('/register', {
            method: 'POST',
            body: JSON.stringify(userData),
        });
    },

    login: async (credentials: { email: string; password: string }) => {
        const response = await apiRequest('/login', {
            method: 'POST',
            body: JSON.stringify({
                email: credentials.email,
                password: credentials.password,
            }),
        });
        
        // Store both tokens after successful login
        if (response.access_token && response.refresh_token) {
            const tokenManager = TokenManager.getInstance();
            tokenManager.storeTokens(response.access_token, response.refresh_token);
        }
        
        return response;
    },

    logout: async () => {
        try {
            // Call logout endpoint to invalidate refresh token
            await authenticatedRequest('/auth/logout', {
                method: 'POST',
            });
        } catch (error) {
            console.error('Logout API call failed:', error);
        } finally {
            // Always clear local tokens
            const tokenManager = TokenManager.getInstance();
            tokenManager.clearTokens();
        }
    },
};

export const profileAPI = {
  getProfile: async () => {
    return authenticatedRequest('/profile', {
      method: 'GET',
    });
  },

  addSubjectCourse: async (data: {
    subject_name: string;
    course_name: string;
    school_name?: string;
    syllabus_url?: string;
    syllabus_file?: File;
  }) => {
    const formData = new FormData();
    formData.append('subject_name', data.subject_name);
    formData.append('course_name', data.course_name);

    if (data.school_name) {
      formData.append('school_name', data.school_name);
    }

    if (data.syllabus_url) {
      formData.append('syllabus_url', data.syllabus_url);
    }

    if (data.syllabus_file) {
      formData.append('syllabus_file', data.syllabus_file);
    }

    return authenticatedRequest('/profile/subject-course', {
      method: 'POST',
      body: formData,
    });
  },

  updateSyllabusFromUrl: async (data: {
    course_id: string;
    syllabus_url: string;
  }) => {
    return authenticatedRequest('/profile/syllabus/update-from-url', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  addManualSyllabus: async (data: {
    course_id: string;
    topics: string[];
    prerequisites?: string[];
    learning_objectives?: string[];
  }) => {
    return authenticatedRequest('/profile/syllabus/manual', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  removeSubject: async (subjectId: string) => {
    return authenticatedRequest(`/profile/subjects/${subjectId}`, {
      method: 'DELETE',
    });
  },

  unarchiveSubject: async (subjectId: string) => {
    return authenticatedRequest(`/profile/subjects/${subjectId}/unarchive`, {
      method: 'POST',
    });
  },

  updateProfile: async (data: { full_name?: string; preferred_class_type?: 'voice' | 'text' }) => {
    return authenticatedRequest('/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  changePassword: async (data: { new_password: string }) => {
    return authenticatedRequest('/profile/password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  uploadProfilePicture: async (file: File) => {
    const formData = new FormData();
    formData.append('profile_picture', file);
    return authenticatedRequest('/profile/picture', {
      method: 'POST',
      body: formData,
    });
  },

  completeTourSection: async (section: string) => {
    return authenticatedRequest(`/profile/tour/${section}`, {
      method: 'POST',
    });
  },

  disableAllTours: async () => {
    return authenticatedRequest('/profile/tour/enabled', {
      method: 'POST',
    });
  },
};

export const lessonAPI = {
  startLesson: async (subjectId: string) => {
    return authenticatedRequest('/lessons/start', {
      method: 'POST',
      body: JSON.stringify({
        subject_id: subjectId,
      }),
    });
  },

  getLessonHistory: async () => {
    return authenticatedRequest('/lessons/history', {
      method: 'GET',
    });
  },

  getLessonById: async (lessonId: string) => {
    return authenticatedRequest(`/lessons/history/${lessonId}`, {
      method: 'GET',
    });
  },

  // Unified interaction method supporting text, image, or both
  interact: async (lessonId: string, options: { message?: string; image?: File }) => {
    const { message, image } = options;
    
    // Validate input
    if (!message && !image) {
      throw new Error('Either message or image must be provided');
    }

    const tokenManager = TokenManager.getInstance();
    const accessToken = tokenManager.getAccessToken();
    const formData = new FormData();
    
    if (message && message.trim()) {
      formData.append('message', message.trim());
    }
    if (image) {
      formData.append('image', image);
    }

    // Use the API client for automatic token refresh handling
    const response = await apiClient.makeRequest(`${API_BASE_URL}/lessons/${lessonId}/interact`, { // lessonId is UUID string
      method: 'POST',
      headers: {
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  },

  // Legacy method for backward compatibility
  interactWithTutor: async (lessonId: string, message: string) => {
    return lessonAPI.interact(lessonId, { message });
  },

  endLesson: async (lessonId: string) => {
    return authenticatedRequest(`/lessons/${lessonId}/end`, {
      method: 'POST',
    });
  },

  // PDF Context Management
  uploadMaterials: async (lessonId: string, files: File[], mode: 'reset' | 'append' = 'reset') => {
    const tokenManager = TokenManager.getInstance();
    const accessToken = tokenManager.getAccessToken();
    const formData = new FormData();

    // Append all files
    files.forEach(file => {
      formData.append('files', file);
    });

    // Append mode
    formData.append('mode', mode);

    const response = await apiClient.makeRequest(`${API_BASE_URL}/lessons/${lessonId}/upload-materials`, { // lessonId is UUID string
      method: 'POST',
      headers: {
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  },

  getLessonContext: async (lessonId: string) => {
    return authenticatedRequest(`/lessons/${lessonId}/context`, {
      method: 'GET',
    });
  },

  deleteMaterial: async (lessonId: string, materialId: string) => {
    return authenticatedRequest(`/lessons/${lessonId}/materials/${materialId}`, {
      method: 'DELETE',
    });
  },
};

export const voiceCallAPI = {
  createSession: async (lessonId: string) => {
    return authenticatedRequest('/voice-call/create-session', {
      method: 'POST',
      body: JSON.stringify({ lesson_id: lessonId }),
    });
  },

  getSessionStatus: async (sessionId: string) => {
    return authenticatedRequest(`/voice-call/${sessionId}/status`, {
      method: 'GET',
    });
  },

  getActiveSessions: async () => {
    return authenticatedRequest('/voice-call/active', {
      method: 'GET',
    });
  },

  endVoiceCall: async (sessionId: string) => {
    return authenticatedRequest(`/voice-call/${sessionId}/end`, {
      method: 'POST',
    });
  },
};

export const parentAPI = {
  createChild: async (fullName: string, email: string, password: string) => {
    const formData = new FormData();
    formData.append('full_name', fullName);
    formData.append('email', email);
    formData.append('password', password);

    return authenticatedRequest('/parent/children', {
      method: 'POST',
      body: formData,
    });
  },

  listChildren: async () => {
    return authenticatedRequest('/parent/children', {
      method: 'GET',
    });
  },

  addSubjectToChild: async (
    childId: string,
    subjectName: string,
    courseName: string,
    schoolName?: string,
    syllabusUrl?: string,
    syllabusFile?: File
  ) => {
    const formData = new FormData();
    formData.append('subject_name', subjectName);
    formData.append('course_name', courseName);

    if (schoolName) {
      formData.append('school_name', schoolName);
    }

    if (syllabusUrl) {
      formData.append('syllabus_url', syllabusUrl);
    }

    if (syllabusFile) {
      formData.append('syllabus_file', syllabusFile);
    }

    return authenticatedRequest(`/parent/children/${childId}/subjects`, {
      method: 'POST',
      body: formData,
    });
  },

  getChildLessons: async (childId: string) => {
    return authenticatedRequest(`/parent/children/${childId}/lessons`, {
      method: 'GET',
    });
  },

  getChildProfile: async (childId: string) => {
    return authenticatedRequest(`/parent/children/${childId}/profile`, {
      method: 'GET',
    });
  },
};

// Subscription API
export const subscriptionAPI = {
  getSubscription: async () => {
    return authenticatedRequest('/subscription', { method: 'GET' });
  },

  createCheckoutSession: async (tier: string, billingCycle: 'monthly' | 'annual', subjectsToKeep?: string[], couponId?: string) => {
    return authenticatedRequest('/subscription/checkout', {
      method: 'POST',
      body: JSON.stringify({ tier, billing_cycle: billingCycle, subjects_to_keep: subjectsToKeep, coupon_id: couponId }),
    });
  },

  changePlan: async (tier: string, billingCycle: 'monthly' | 'annual', subjectsToKeep?: string[]) => {
    return authenticatedRequest('/subscription/change-plan', {
      method: 'POST',
      body: JSON.stringify({ tier, billing_cycle: billingCycle, subjects_to_keep: subjectsToKeep }),
    });
  },

  createPortalSession: async () => {
    return authenticatedRequest('/subscription/portal', {
      method: 'POST',
    });
  },

  cancelSubscription: async () => {
    return authenticatedRequest('/subscription/cancel', {
      method: 'POST',
    });
  },
};

export const progressReportAPI = {
  generateReport: async (data: { report_type?: string; days?: number }) => {
    return authenticatedRequest('/progress-reports/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getReports: async (limit: number = 10, offset: number = 0) => {
    return authenticatedRequest(`/progress-reports?limit=${limit}&offset=${offset}`, {
      method: 'GET',
    });
  },

  getReportById: async (reportId: string) => {
    return authenticatedRequest(`/progress-reports/${reportId}`, {
      method: 'GET',
    });
  },
};
